/**
 * Cuentas que se quedan sin créditos, con la **fecha proyectada de agotamiento**.
 *
 * Es el bloque que dispara la conversación comercial antes de que el servicio se
 * corte, y el que más depende de la regla de números derivables: una fecha a 23
 * días sin sus dos insumos al lado es una predicción que nadie puede discutir.
 * Así que la tabla publica los tres términos de la cuenta:
 *
 *   saldo ÷ quema mensual = meses de autonomía → fecha
 *
 * y además **los meses exactos que promedió la quema**, con su consumo. El
 * promedio se hace solo sobre los meses medidos y por eso dice cuántos son:
 * contar un mes sin dato como cero bajaría la quema y correría la fecha hacia
 * adelante, que es justo el error que deja a un cliente sin servicio sin aviso.
 *
 * **El saldo no se deriva de comprados − consumidos**, aunque los tres números
 * estén en la misma fila. Lo informa la plataforma y arrastra créditos de
 * períodos anteriores; calcularlo daría un número distinto del que ve el cliente
 * en su cuenta. Los otros dos van igual, como base.
 *
 * **La suscripción de Stripe es la única columna con rol.** Para ops y comercial
 * el servidor no manda `stripeSubscriptionId`, así que dibujar la celda les
 * mostraría "sin vincular" sobre una suscripción que sí existe — una afirmación
 * que los datos contradicen. La columna no se dibuja para ellos en vez de
 * mentirles. Para admin sí, con su acción de vincular cuando falta de verdad.
 */
import type { CreditBalance, Links } from '@ops/contracts'
import { formatMetricValue } from '@ops/contracts'

import { plural } from '../../lib/format'
import { rutaCliente } from '../../lib/rutas'
import { useVincular } from '../../lib/vincular'
import {
  Button,
  DataTable,
  ExternalLink,
  MissingCell,
  Panel,
  ProvenanceBadge,
  StatusPill,
  type Column,
} from '../../ui'
import { Vacio } from '../comunes'
import {
  explicacionProyeccion,
  explicacionQuema,
  formatFechaDia,
  severidadDeAgotamiento,
} from './derivar'
import { Link } from 'react-router-dom'

export interface CreditosBlockProps {
  cuentas: readonly CreditBalance[]
  links: Links
  umbralAgotamientoMeses: number
  /** Dibuja la columna de la suscripción de Stripe. Ver el comentario de arriba. */
  esAdmin: boolean
  onCrearSeguimiento: (cuenta: CreditBalance) => void
  now: Date
  id?: string
}

export function CreditosBlock({
  cuentas,
  links,
  umbralAgotamientoMeses,
  esAdmin,
  onCrearSeguimiento,
  now,
  id,
}: CreditosBlockProps) {
  const vincular = useVincular()

  const columnas: ReadonlyArray<Column<CreditBalance>> = [
    {
      key: 'cuenta',
      header: 'Cuenta',
      rowHeader: true,
      width: '190px',
      sortValue: (c) => c.clientNombre,
      filterValue: (c) => c.clientNombre,
      render: (c) => (
        <Link
          to={rutaCliente(c.clientId)}
          className="truncate font-semibold text-accent-text underline decoration-from-font underline-offset-2 hover:text-text"
        >
          {c.clientNombre}
        </Link>
      ),
    },
    {
      key: 'saldo',
      header: 'Saldo de créditos',
      align: 'right',
      width: '150px',
      sortValue: (c) => c.creditosRestantes,
      render: (c) =>
        c.creditosRestantes === null ? (
          /* Sin saldo informado no hay proyección posible, y un 0 diría que la
             cuenta ya se quedó sin créditos. */
          <MissingCell que="saldo de la plataforma" />
        ) : (
          <span className="tabular">
            {formatMetricValue(c.creditosRestantes, 'count')}
          </span>
        ),
    },
    {
      key: 'comprados',
      header: 'Comprados · consumidos',
      align: 'right',
      width: '190px',
      sortValue: (c) => c.creditosComprados,
      render: (c) => (
        /* La base del saldo, aunque el saldo no sea su resta: los dos números
           que el cliente ve en su factura y en su cuenta. */
        <span className="tabular text-micro text-muted-strong">
          {c.creditosComprados === null
            ? 'sin dato'
            : formatMetricValue(c.creditosComprados, 'count')}{' '}
          ·{' '}
          {c.creditosConsumidos === null
            ? 'sin dato'
            : formatMetricValue(c.creditosConsumidos, 'count')}
        </span>
      ),
    },
    {
      key: 'quema',
      header: 'Quema mensual',
      align: 'right',
      width: '220px',
      sortValue: (c) => c.quema?.promedioMensual ?? null,
      render: (c) =>
        c.quema === null ? (
          <MissingCell que="mes medido para promediar" />
        ) : (
          <span className="flex flex-col items-end gap-0.5">
            <span className="tabular font-semibold">
              {formatMetricValue(c.quema.promedioMensual, 'count')}
            </span>
            {/* Los meses exactos que entraron en el promedio: la base de la base. */}
            <span
              data-testid="quema-base"
              className="tabular text-micro text-muted-strong"
            >
              {explicacionQuema(c.quema)}
            </span>
          </span>
        ),
    },
    {
      key: 'agotamiento',
      header: 'Se agota el',
      width: '260px',
      sortValue: (c) => c.proyeccion?.dias ?? null,
      render: (c) =>
        c.proyeccion === null ? (
          /* Dos motivos distintos y los dos honestos: falta el saldo, o la
             cuenta no quema nada y entonces no se agota nunca. */
          <span className="flex flex-col gap-0.5">
            <span className="text-micro text-muted-strong">
              Sin proyección
            </span>
            <span className="max-w-prosa text-micro text-muted-strong">
              {c.creditosRestantes === null
                ? 'Falta el saldo informado por la plataforma.'
                : 'La cuenta no registra quema: un saldo que no se consume no se agota.'}
            </span>
          </span>
        ) : (
          <span className="flex flex-col gap-0.5">
            <span className="flex items-center gap-2">
              <span className="tabular font-semibold">
                {formatFechaDia(c.proyeccion.fecha)}
              </span>
              {/* El color nunca solo: la autonomía va escrita en la pill. */}
              <StatusPill
                severidad={severidadDeAgotamiento(
                  c.proyeccion.meses,
                  umbralAgotamientoMeses,
                )}
                size="sm"
                testId="autonomia-pill"
              >
                {formatMetricValue(c.proyeccion.meses, 'months')}
              </StatusPill>
            </span>
            {/* La aritmética entera, para que la fecha sea recalculable a mano. */}
            <span
              data-testid="proyeccion-base"
              className="tabular text-micro text-muted-strong"
            >
              {explicacionProyeccion(c.proyeccion)} ·{' '}
              {formatMetricValue(c.proyeccion.dias, 'count')}{' '}
              {plural(c.proyeccion.dias, 'día', 'días')}
            </span>
          </span>
        ),
    },
    ...(esAdmin
      ? [
          {
            key: 'stripe',
            header: 'Suscripción',
            width: '180px',
            render: (c: CreditBalance) => (
              <ExternalLink
                link={links.clientes[c.clientId]?.stripeSubscription ?? null}
                variante="texto"
                sistemaFaltante="Stripe"
                onVincular={() =>
                  vincular(`la suscripción de ${c.clientNombre} en Stripe`)
                }
              >
                Ver la suscripción
              </ExternalLink>
            ),
          } satisfies Column<CreditBalance>,
        ]
      : []),
    {
      key: 'fuente',
      header: 'Fuente',
      width: '170px',
      render: (c) => (
        <ProvenanceBadge
          provenance={c.provenance}
          now={now}
          variant="inline"
          hrefAdmin="/admin"
        />
      ),
    },
    {
      key: 'accion',
      header: 'Seguimiento',
      width: '150px',
      render: (c) => (
        <Button variante="chico" icono="mas" onClick={() => onCrearSeguimiento(c)}>
          Asignar seguimiento
          <span className="sr-only"> de los créditos de {c.clientNombre}</span>
        </Button>
      ),
    },
  ]

  if (cuentas.length === 0) {
    return (
      <div id={id} className="min-w-0">
        <Panel titulo="Cuentas por quedarse sin créditos" padding="sm">
          <Vacio
            icono={null}
            mensaje={`Ninguna cuenta baja de ${formatMetricValue(umbralAgotamientoMeses, 'months')} de autonomía.`}
            detalle="Entran acá las cuentas con saldo informado y quema medida: una cuenta sin saldo cargado no se puede proyectar y no aparece."
          />
        </Panel>
      </div>
    )
  }

  const primera = cuentas[0]

  return (
    <div id={id} className="min-w-0">
      <Panel
        titulo="Cuentas por quedarse sin créditos"
        subtitulo={
          primera === undefined || primera.proyeccion === null ? (
            'Ordenadas por cercanía del agotamiento.'
          ) : (
            <>
              La primera es <b>{primera.clientNombre}</b>: se agota el{' '}
              <b>{formatFechaDia(primera.proyeccion.fecha)}</b>, en{' '}
              <b>
                {formatMetricValue(primera.proyeccion.dias, 'count')}{' '}
                {plural(primera.proyeccion.dias, 'día', 'días')}
              </b>
              .
            </>
          )
        }
        severidad="warning"
        padding="none"
        footer={
          <>
            Entra acá toda cuenta con menos de{' '}
            <b>{formatMetricValue(umbralAgotamientoMeses, 'months')}</b> de
            autonomía. La fecha es <b>saldo ÷ quema promedio</b> proyectado sobre
            meses de 30 días, y los tres términos están en la fila para poder
            rehacerla. El saldo lo informa la plataforma y arrastra créditos de
            períodos anteriores: <b>no es comprados − consumidos</b>.
          </>
        }
      >
        <DataTable
          columns={columnas}
          rows={cuentas}
          rowKey={(c) => c.clientId}
          rowSeverity={(c) =>
            severidadDeAgotamiento(c.proyeccion?.meses, umbralAgotamientoMeses)
          }
          densidadPorSeveridad
          filtroPlaceholder="cuenta"
          emptyMessage="Ninguna cuenta está por agotar sus créditos"
          caption={
            <>
              Saldo, quema promedio y fecha proyectada de agotamiento. El saldo y
              los créditos comprados salen de la plataforma; la quema, del consumo
              medido.
            </>
          }
        />
      </Panel>
    </div>
  )
}
